import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, ComposedChart
} from 'recharts';

// Educational Icons
const TrendingUpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
    <polyline points="17 6 23 6 23 12"></polyline>
  </svg>
);

const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const BookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
  </svg>
);

const BuildingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
    <path d="M6 12h4v4h-4v-4"></path>
    <path d="M14 12h4v4h-4v-4"></path>
    <path d="M6 20h4v2h-4v-2"></path>
    <path d="M14 20h4v2h-4v-2"></path>
  </svg>
);

const DollarSignIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23"></line>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
  </svg>
);

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const TargetIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"></circle>
    <circle cx="12" cy="12" r="6"></circle>
    <circle cx="12" cy="12" r="2"></circle>
  </svg>
);

const RefreshIcon = ({ spinning }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{
      animation: spinning ? 'spin 1s linear infinite' : 'none',
    }}
  >
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
  </svg>
);

const DownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7,10 12,15 17,10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

const StatCard = ({ title, value, subtitle, icon: Icon, color, bgColor, trend, trendValue }) => {
  return (
    <div style={{
      backgroundColor: bgColor,
      borderRadius: '12px',
      padding: '20px',
      color: 'white',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s',
      cursor: 'pointer'
    }}
    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div style={{
          padding: '8px',
          borderRadius: '8px',
          backgroundColor: 'rgba(255,255,255,0.2)'
        }}>
          <Icon />
        </div>
        {trend && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '12px',
            opacity: 0.9
          }}>
            <TrendingUpIcon />
            <span>{trendValue}%</span>
          </div>
        )}
      </div>
      <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '4px' }}>
        {value}
      </div>
      <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: '12px', opacity: 0.7 }}>
          {subtitle}
        </div>
      )}
    </div>
  );
};

const KPICard = ({ title, value, target, icon: Icon, color }) => {
  const percentage = target > 0 ? Math.min((value / target) * 100, 100) : 0;
  const isAchieved = value >= target;

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      border: `2px solid ${isAchieved ? '#10B981' : '#E5E7EB'}`
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div style={{
          padding: '8px',
          borderRadius: '8px',
          backgroundColor: `${color}20`,
          color: color
        }}>
          <Icon />
        </div>
        <div style={{
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          backgroundColor: isAchieved ? '#D1FAE5' : '#FEF3C7',
          color: isAchieved ? '#065F46' : '#92400E'
        }}>
          {isAchieved ? 'Achieved' : `${percentage.toFixed(1)}%`}
        </div>
      </div>

      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '4px' }}>
          {title}
        </div>
        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
      </div>

      <div style={{ backgroundColor: '#E5E7EB', borderRadius: '4px', height: '6px' }}>
        <div style={{
          backgroundColor: isAchieved ? '#10B981' : color,
          height: '100%',
          borderRadius: '4px',
          width: `${percentage}%`,
          transition: 'width 0.3s ease'
        }} />
      </div>

      <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>
        Target: {typeof target === 'number' ? target.toLocaleString() : target}
      </div>
    </div>
  );
};

const AnalyticsDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';

  // Fetch comprehensive analytics
  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      // Fetch all data concurrently
      const [
        studentsRes,
        coursesRes,
        lecturersRes,
        campusesRes,
        paymentsRes,
        attendanceRes
      ] = await Promise.allSettled([
        fetch(`${API_BASE_URL}/students`, { headers }),
        fetch(`${API_BASE_URL}/courses`, { headers }),
        fetch(`${API_BASE_URL}/lecturers`, { headers }),
        fetch(`${API_BASE_URL}/campuses`, { headers }),
        fetch(`${API_BASE_URL}/payments`, { headers }),
        fetch(`${API_BASE_URL}/attendance`, { headers })
      ]);

      // Process data
      const students = studentsRes.status === 'fulfilled' && studentsRes.value.ok
        ? await studentsRes.value.json() : [];
      const courses = coursesRes.status === 'fulfilled' && coursesRes.value.ok
        ? await coursesRes.value.json() : [];
      const lecturers = lecturersRes.status === 'fulfilled' && lecturersRes.value.ok
        ? await lecturersRes.value.json() : [];
      const campuses = campusesRes.status === 'fulfilled' && campusesRes.value.ok
        ? await campusesRes.value.json() : [];
      const payments = paymentsRes.status === 'fulfilled' && paymentsRes.value.ok
        ? await paymentsRes.value.json() : [];
      const attendance = attendanceRes.status === 'fulfilled' && attendanceRes.value.ok
        ? await attendanceRes.value.json() : [];

      // Calculate KPIs
      const totalRevenue = payments
        .filter(p => p.status === 'confirmed')
        .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

      const confirmedPayments = payments.filter(p => p.status === 'confirmed').length;
      const totalStudents = students.length;
      const totalLecturers = lecturers.length;
      const totalCourses = courses.length;
      const totalCampuses = campuses.length;

      // Calculate attendance rate
      const presentRecords = Array.isArray(attendance) ? attendance.filter(a => a.status === 'present').length : 0;
      const totalAttendanceRecords = Array.isArray(attendance) ? attendance.length : 0;
      const attendanceRate = totalAttendanceRecords > 0 ? (presentRecords / totalAttendanceRecords) * 100 : 0;

      // Generate enrollment trends (last 12 months)
      const enrollmentTrends = [];
      for (let i = 11; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        // Simulate growth trends (in real app, this would be historical data)
        const baseStudents = totalStudents * 0.7;
        const growth = (i / 11) * 0.3; // Linear growth over 12 months
        const students = Math.floor(baseStudents * (1 + growth));

        enrollmentTrends.push({
          month: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          students: students,
          lecturers: Math.floor(totalLecturers * (0.8 + growth * 0.4)),
          courses: Math.floor(totalCourses * (0.9 + growth * 0.2)),
          revenue: totalRevenue * (0.6 + growth * 0.8) // Revenue growth
        });
      }

      // Department distribution
      const courseCategories = courses.reduce((acc, course) => {
        let category = 'General Studies';
        if (course.name.toLowerCase().includes('theology') || course.name.toLowerCase().includes('biblical')) {
          category = 'Theology';
        } else if (course.name.toLowerCase().includes('pastoral') || course.name.toLowerCase().includes('ministry')) {
          category = 'Pastoral Care';
        } else if (course.name.toLowerCase().includes('leadership') || course.name.toLowerCase().includes('church')) {
          category = 'Church Leadership';
        }
        acc[category] = (acc[category] || 0) + 1;
        return acc;
      }, {});

      const departmentData = Object.entries(courseCategories).map(([name, count]) => ({
        name,
        value: count,
        percentage: totalCourses > 0 ? ((count / totalCourses) * 100).toFixed(1) : 0
      }));

      // Campus performance
      const campusPerformance = campuses.map(campus => {
        const campusStudents = students.filter(s => s.campusId === campus.id).length;
        const campusRevenue = payments
          .filter(p => p.Student?.campusId === campus.id && p.status === 'confirmed')
          .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

        return {
          name: campus.name,
          students: campusStudents,
          revenue: campusRevenue,
          occupancy: campus.capacity > 0 ? Math.round((campusStudents / campus.capacity) * 100) : 0
        };
      });

      // Performance metrics
      const avgStudentsPerCampus = totalCampuses > 0 ? Math.round(totalStudents / totalCampuses) : 0;
      const avgRevenuePerCampus = totalCampuses > 0 ? Math.round(totalRevenue / totalCampuses) : 0;
      const studentToLecturerRatio = totalLecturers > 0 ? (totalStudents / totalLecturers).toFixed(1) : 0;

      setAnalytics({
        kpis: {
          totalStudents,
          totalLecturers,
          totalCourses,
          totalCampuses,
          totalRevenue,
          confirmedPayments,
          attendanceRate: attendanceRate.toFixed(1),
          avgStudentsPerCampus,
          avgRevenuePerCampus,
          studentToLecturerRatio
        },
        trends: enrollmentTrends,
        departmentData,
        campusPerformance,
        targets: {
          studentTarget: Math.round(totalStudents * 1.2), // 20% growth target
          revenueTarget: Math.round(totalRevenue * 1.15), // 15% growth target
          attendanceTarget: 85, // 85% attendance target
          lecturerTarget: Math.round(totalLecturers * 1.1) // 10% growth target
        }
      });

    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
    setTimeout(() => setRefreshing(false), 500);
  };

  const handleExport = () => {
    const reportData = {
      generatedAt: new Date().toISOString(),
      timeRange,
      analytics,
      summary: {
        totalStudents: analytics?.kpis.totalStudents,
        totalRevenue: analytics?.kpis.totalRevenue,
        attendanceRate: analytics?.kpis.attendanceRate,
        totalCampuses: analytics?.kpis.totalCampuses
      }
    };

    const dataStr = JSON.stringify(reportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);

    const exportFileDefaultName = `analytics-report-${new Date().toISOString().split('T')[0]}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  if (loading && !analytics) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '60px',
        backgroundColor: 'white',
        borderRadius: '16px',
        margin: '20px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #F3F4F6',
          borderTop: '4px solid #8B5CF6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginRight: '16px',
        }} />
        <span style={{ fontSize: '16px', color: '#6B7280' }}>Loading analytics dashboard...</span>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 50%, #E2E8F0 100%)',
      padding: '20px'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #8B5CF6 30%, #06B6D4 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: '0 0 8px 0',
            }}>
              Analytics Dashboard
            </h1>
            <p style={{ margin: 0, color: '#6B7280', fontSize: '16px' }}>
              Comprehensive institutional insights and performance metrics
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                outline: 'none',
              }}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>

            <button
              onClick={handleRefresh}
              style={{
                padding: '12px 16px',
                backgroundColor: '#F3F4F6',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#E5E7EB'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#F3F4F6'}
            >
              <RefreshIcon spinning={refreshing} />
              Refresh
            </button>

            <button
              onClick={handleExport}
              style={{
                padding: '12px 16px',
                backgroundColor: '#8B5CF6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#7C3AED'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#8B5CF6'}
            >
              <DownloadIcon />
              Export Report
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        {analytics && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px'
          }}>
            <KPICard
              title="Total Students"
              value={analytics.kpis.totalStudents}
              target={analytics.targets.studentTarget}
              icon={UsersIcon}
              color="#8B5CF6"
            />
            <KPICard
              title="Total Revenue"
              value={analytics.kpis.totalRevenue}
              target={analytics.targets.revenueTarget}
              icon={DollarSignIcon}
              color="#10B981"
            />
            <KPICard
              title="Attendance Rate"
              value={parseFloat(analytics.kpis.attendanceRate)}
              target={analytics.targets.attendanceTarget}
              icon={CalendarIcon}
              color="#F59E0B"
            />
            <KPICard
              title="Faculty Members"
              value={analytics.kpis.totalLecturers}
              target={analytics.targets.lecturerTarget}
              icon={UsersIcon}
              color="#06B6D4"
            />
          </div>
        )}
      </div>

      {/* Main Analytics */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px', marginBottom: '24px' }}>
        {/* Enrollment Trends */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
            Institutional Growth Trends
          </h3>
          <div style={{ height: '350px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={analytics?.trends || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" stroke="#6B7280" />
                <YAxis yAxisId="left" stroke="#6B7280" />
                <YAxis yAxisId="right" orientation="right" stroke="#10B981" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white'
                  }}
                />
                <Bar yAxisId="left" dataKey="students" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="left" dataKey="lecturers" fill="#06B6D4" radius={[4, 4, 0, 0]} />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="revenue"
                  stroke="#10B981"
                  strokeWidth={3}
                  dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Department Distribution */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
            Academic Programs Distribution
          </h3>
          <div style={{ height: '200px', marginBottom: '20px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={analytics?.departmentData || []}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {analytics?.departmentData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={`hsl(${index * 60}, 70%, 50%)`} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {analytics?.departmentData?.map((dept, index) => (
              <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: `hsl(${index * 60}, 70%, 50%)`,
                  }} />
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    {dept.name}
                  </span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {dept.value}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6B7280' }}>
                    {dept.percentage}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Campus Performance */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
          Campus Performance Overview
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          {analytics?.campusPerformance?.map((campus, index) => (
            <div key={index} style={{
              padding: '20px',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              backgroundColor: '#F9FAFB'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                  {campus.name}
                </h4>
                <div style={{
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  backgroundColor: campus.occupancy > 80 ? '#D1FAE5' : campus.occupancy > 60 ? '#FEF3C7' : '#FEE2E2',
                  color: campus.occupancy > 80 ? '#065F46' : campus.occupancy > 60 ? '#92400E' : '#991B1B'
                }}>
                  {campus.occupancy}% occupied
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Students</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                    {campus.students}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Revenue</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                    KSH {campus.revenue.toLocaleString()}
                  </div>
                </div>
              </div>

              <div style={{ backgroundColor: '#E5E7EB', borderRadius: '4px', height: '6px' }}>
                <div style={{
                  backgroundColor: campus.occupancy > 80 ? '#EF4444' : campus.occupancy > 60 ? '#F59E0B' : '#10B981',
                  height: '100%',
                  borderRadius: '4px',
                  width: `${campus.occupancy}%`,
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Metrics Summary */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
          Key Performance Indicators
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px'
        }}>
          <StatCard
            title="Students per Campus"
            value={analytics?.kpis.avgStudentsPerCampus.toString()}
            subtitle="Average distribution"
            icon={BuildingIcon}
            color="#8B5CF6"
            bgColor="linear-gradient(135deg, #8B5CF6 0%, #A855F7 100%)"
          />
          <StatCard
            title="Revenue per Campus"
            value={`KSH ${analytics?.kpis.avgRevenuePerCampus.toLocaleString()}`}
            subtitle="Average earnings"
            icon={DollarSignIcon}
            color="#10B981"
            bgColor="linear-gradient(135deg, #10B981 0%, #34D399 100%)"
          />
          <StatCard
            title="Student-Lecturer Ratio"
            value={`${analytics?.kpis.studentToLecturerRatio}:1`}
            subtitle="Teaching load indicator"
            icon={UsersIcon}
            color="#06B6D4"
            bgColor="linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)"
          />
          <StatCard
            title="Active Programs"
            value={analytics?.kpis.totalCourses.toString()}
            subtitle="Academic offerings"
            icon={BookIcon}
            color="#F59E0B"
            bgColor="linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)"
          />
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AnalyticsDashboard;
